/**
 * The ladder shops' gear with fixed affix pools (docs/systems/ITEMS_V2.md §3 Arena, R7): the slow, sure path. Each ware names a
 * base, a pool of affixes and a count; the buyer picks that many from the pool, and the piece comes with exactly
 * those, at a fixed quality (no `Rng`). Paid in ladder points (`LadderDef.points`). Bought pieces are ordinary rolls:
 * they forge, hone, socket and trade like any other.
 */
import { affixValue, AFFIXES, AFFIX_COUNT, budgetShare, clampIlv, type AffixRoll } from './affixes';
import { composeRoll, type ItemRoll, type Rarity } from './items';
import { CRAFT_MATS } from './itemCraft';
import type { LadderId } from './ladder';

export interface ArenaGearWare {
  readonly id: string;
  readonly ladder: LadderId;
  /** The base item (data/items.ts, data/gear.ts). */
  readonly base: string;
  readonly ilv: number;
  readonly rarity: Rarity;
  /** The affix ids the buyer picks from (no art / school affixes: they need a target). */
  readonly pool: readonly string[];
  /** Quality of every picked line, 0–100: a sure roll a little above the middle, never the top. */
  readonly quality: number;
  /** Ladder points. */
  readonly price: number;
}

const DEF = ['maxHp', 'def', 'magicDef', 'counterDmg', 'hpRegen', 'critRes', 'toughness', 'guardHp'] as const;
const HIT = ['atk', 'hit', 'crit', 'critDmg', 'defPierce', 'guardBreak', 'counterDmg', 'magic'] as const;

const gear = (
  ladder: LadderId,
  id: string,
  base: string,
  pool: readonly string[],
  price: number,
  rarity: Rarity = 'epic',
): ArenaGearWare => ({ id, ladder, base, ilv: 30, rarity, pool, quality: 70, price });

export const ARENA_GEAR: readonly ArenaGearWare[] = [
  gear('heaven', 'arena-heaven-blade', 'azure-sword', HIT, 1200),
  gear('heaven', 'arena-heaven-robe', 'crimson-coat', DEF, 1000),
  gear('heaven', 'arena-heaven-ring', 'jade-ring', ['atk', 'hit', 'crit', 'critDmg', 'magic', 'magicHit', 'defPierce', 'lifesteal'], 800),
  gear('hell', 'arena-hell-sabre', 'great-dao', HIT, 1200),
  gear('hell', 'arena-hell-vest', 'black-vest', DEF, 1000),
  gear('hell', 'arena-hell-pendant', 'cloud-pendant', ['magic', 'magicHit', 'magicCrit', 'maxQi', 'qiRegen', 'cdPct', 'magicDef', 'toughness'], 800),
];

export const ARENA_GEAR_BY_ID: Readonly<Record<string, ArenaGearWare>> = Object.fromEntries(
  ARENA_GEAR.map((w) => [w.id, w]),
);

/** A ladder's gear wares. */
export const arenaGearOf = (ladder: LadderId): readonly ArenaGearWare[] => ARENA_GEAR.filter((w) => w.ladder === ladder);

/** How many affixes the buyer picks (the rarity's count). */
export const arenaPickCount = (w: ArenaGearWare): number => AFFIX_COUNT[w.rarity][0];

/**
 * The roll a purchase gives, or why the picks are refused: the right count, each from the pool, no two of one group.
 * The same picks always give the same piece.
 */
export function arenaGearRoll(w: ArenaGearWare, picks: readonly string[]): ItemRoll | string {
  const n = arenaPickCount(w);
  if (picks.length !== n) return `เลือกค่าให้ครบ ${n} บรรทัด`;
  const groups = new Set<string>();
  const share = budgetShare(w.rarity, n);
  const lv = clampIlv(w.ilv);
  const affixes: AffixRoll[] = [];
  for (const id of picks) {
    const a = AFFIXES[id];
    if (!a || !w.pool.includes(id)) return 'ค่าที่เลือกไม่อยู่ในชุดของชิ้นนี้';
    const g = a.group ?? a.id;
    if (groups.has(g)) return 'เลือกค่าชนิดเดียวกันซ้ำไม่ได้';
    groups.add(g);
    affixes.push({ id, v: affixValue(a, lv, w.quality, share), q: w.quality });
  }
  return composeRoll(w.base, lv, w.rarity, affixes);
}

/** Goods the ladder shops sell for points (not rolled gear): ยันต์กันถอย. */
export interface ArenaGoodWare {
  readonly id: string;
  readonly good: string;
  readonly n: number;
  readonly price: number;
}
export const ARENA_GOODS: readonly ArenaGoodWare[] = [{ id: 'arena-protect-charm', good: CRAFT_MATS.protect, n: 1, price: 150 }];
export const ARENA_GOOD_BY_ID: Readonly<Record<string, ArenaGoodWare>> = Object.fromEntries(ARENA_GOODS.map((w) => [w.id, w]));
